import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';
import uuid4 from 'uuid4';
import Container from '@mui/material/Container';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { CircularProgress } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { alpha } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { GridFilterItem } from '@mui/x-data-grid-premium';
import { CARD_BORDER } from '../../components/Section';
import COLORS from '../../constants/colors';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useSearchParamsState } from '../../hooks';
import { appConfigSelector } from '../../redux/modules/appConfig/appConfig.selectors';
import { UserPortfolioRequestTable } from './UserPortfolioRequestTable';
import { UserPortfolioRequestType } from '../../types/userPortfolioRequest';
import { MTabPanel } from '../../components/TabPanel';
import { MultiSelectFilter } from '../../components/QuickFilter';
import { authSelector } from '../../redux/features/auth/auth.selectors';
import { isUserPortfolioRequestLoadingSelector } from '../../redux/modules/userPortfolioRequest/userPortfolioRequest.selectors';

const mapTabParamToValue: { [prop: string]: string } = {
  subscription: '1',
  redemption: '2',
  default: '1',
};
const mapTabValueToTabParam: { [prop: string]: string } = {
  1: 'subscription',
  2: 'redemption',
};

/** Filter group label, matching the field labels used across the detail pages. */
const filterLabelSx = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: COLORS.LIGHT_GRAY,
  whiteSpace: 'nowrap',
};

/** Reset affordance: quiet until there is actually something to reset. */
const resetButtonSx = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: COLORS.DARK_GREY,
  whiteSpace: 'nowrap',
  minWidth: 'auto',
  px: '10px',
  '&:hover': { backgroundColor: alpha(COLORS.MAIN_DARK, 0.04) },
  '& .MuiButton-startIcon': { mr: '6px' },
  '& .MuiSvgIcon-root': { fontSize: '18px' },
};

export function UserPortfolioRequestList() {
  const [products, setProducts] = useState([]);
  const [fundCategories, setFundCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedFundCategories, setSelectedFundCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [initiateFilters, setInitiateFilters] = useState<boolean>(true);
  /**
   * Held false until saved filters have been restored, so the grid's first
   * fetch is already filtered. Previously the page fetched unfiltered, then
   * refetched 3.5s later once a timer applied the restored filters — which
   * showed rows that were then replaced by the real, filtered result.
   */
  const [filtersReady, setFiltersReady] = useState<boolean>(false);
  const [productsExternalFilter, setProductsExternalFilter] = useState<GridFilterItem | object>({});
  const [fundCategoriesExternalFilter, setFundCategoriesExternalFilter] = useState<GridFilterItem | object>({});
  const [statusesExternalFilter, setStatusesExternalFilter] = useState<GridFilterItem | object>({});
  const [selectedTab, setSelectedTab] = useSearchParamsState('tab', 'default');
  // const [loading, setLoading] = useState(false);
  const isUserPortfolioRequestLoading = useSelector(isUserPortfolioRequestLoadingSelector);
  const { fetchStatus, updateStatus } = useSelector(appConfigSelector);
  const activeFilterCount = selectedProducts.length + selectedFundCategories.length + selectedStatuses.length;
  const { user } = useSelector(authSelector);
  let filters = localStorage.getItem(`userPortfolioRequestListFiltersSelection${mapTabParamToValue[selectedTab]}`);

  const isLoading = fetchStatus === fetchStatuses.pending || updateStatus === fetchStatuses.pending;

  const setDataToStorage = (data, currentTab) => {
    if (!user?.id || initiateFilters) return;
    const parsedFilters = filters ? JSON.parse(filters)[user?.id] : {};

    localStorage.setItem(
      `userPortfolioRequestListFiltersSelection${mapTabParamToValue[currentTab || selectedTab]}`,
      JSON.stringify({
        [user?.id]: { ...parsedFilters, ...data },
      }),
    );
  };
  const onSelectProduct = (filter, currentTab = '') => {
    // setLoading(true);
    switch (filter) {
      case 'Select All':
        // eslint-disable-next-line no-case-declarations
        const allProducts = products.filter((item) => item !== 'Select All' && item !== 'Clear All');
        setSelectedProducts(allProducts);
        if (currentTab) setDataToStorage({ selectedProducts: allProducts }, currentTab);
        setProductsExternalFilter({
          field: 'userPortfolio.subscribedPortfolio.productType.nameEn',
          operator: 'isAnyOf',
          id: uuid4(),
          value: allProducts,
        });
        break;

      case 'Clear All':
        setSelectedProducts([]);
        if (currentTab) setDataToStorage({ selectedProducts: [] }, currentTab);

        setProductsExternalFilter({});
        break;

      default: {
        let newSelectedProducts = selectedProducts.includes(filter)
          ? selectedProducts.filter((item) => item !== filter)
          : [...selectedProducts, filter];
        if (currentTab) setDataToStorage({ selectedProducts: newSelectedProducts }, currentTab);
        else if (selectedProducts.length) newSelectedProducts = [filter];
        else newSelectedProducts = [...selectedProducts, filter];

        setSelectedProducts(newSelectedProducts);
        setProductsExternalFilter({
          field: 'userPortfolio.subscribedPortfolio.productType.nameEn',
          operator: 'isAnyOf',
          id: uuid4(),
          value: newSelectedProducts,
        });
      }
    }
  };

  const onSelectFundCategory = (filter, currentTab = '') => {
    // setLoading(true);
    switch (filter) {
      case 'Select All': {
        const allFundCategories = fundCategories.filter((item) => item !== 'Select All' && item !== 'Clear All');
        setSelectedFundCategories(allFundCategories);
        if (currentTab) setDataToStorage({ selectedFundCategories: allFundCategories }, currentTab);
        setFundCategoriesExternalFilter({
          field: 'fundCategory.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: allFundCategories,
        });
        break;
      }

      case 'Clear All':
        setSelectedFundCategories([]);
        if (currentTab) setDataToStorage({ selectedFundCategories: [] }, currentTab);
        setFundCategoriesExternalFilter({});
        break;

      default: {
        let newSelectedFundCategories = selectedFundCategories.includes(filter)
          ? selectedFundCategories.filter((item) => item !== filter)
          : [...selectedFundCategories, filter];
        if (currentTab) setDataToStorage({ selectedFundCategories: newSelectedFundCategories }, currentTab);
        else if (selectedFundCategories.length) newSelectedFundCategories = [filter];
        else newSelectedFundCategories = [...selectedFundCategories, filter];

        setSelectedFundCategories(newSelectedFundCategories);
        setFundCategoriesExternalFilter({
          field: 'fundCategory.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: newSelectedFundCategories,
        });
      }
    }
  };

  const onSelectStatus = (filter, currentTab = '') => {
    // setLoading(true);
    switch (filter) {
      case 'Select All':
        // eslint-disable-next-line no-case-declarations
        const allStatuses = statuses.filter((item) => item !== 'Select All' && item !== 'Clear All');
        setSelectedStatuses(allStatuses);
        if (currentTab) setDataToStorage({ selectedStatuses: allStatuses }, currentTab);
        setStatusesExternalFilter({
          field: 'status.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: allStatuses,
        });
        break;

      case 'Clear All':
        setSelectedStatuses([]);
        if (currentTab) setDataToStorage({ selectedStatuses: [] }, currentTab);

        setStatusesExternalFilter({});
        break;

      default: {
        let newSelectedStatuses = selectedStatuses.includes(filter)
          ? selectedStatuses.filter((item) => item !== filter)
          : [...selectedStatuses, filter];
        if (currentTab) setDataToStorage({ selectedStatuses: newSelectedStatuses }, currentTab);
        else if (selectedStatuses.length) newSelectedStatuses = [filter];
        else newSelectedStatuses = [...selectedStatuses, filter];

        setSelectedStatuses(newSelectedStatuses);
        setStatusesExternalFilter({
          field: 'status.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: newSelectedStatuses,
        });
      }
    }
  };

  const clearAllFilters = () => {
    onSelectProduct('Clear All');
    onSelectFundCategory('Clear All');
    onSelectStatus('Clear All');
  };

  /**
   * User-facing reset. clearAllFilters() only clears the in-memory selection
   * (its handlers skip persistence when called without a tab), so the stored
   * selection is dropped too — otherwise a reload would bring the filters back.
   */
  const resetFilters = () => {
    clearAllFilters();
    localStorage.removeItem(`userPortfolioRequestListFiltersSelection${mapTabParamToValue[selectedTab]}`);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    // setLoading(true);
    setSelectedTab(mapTabValueToTabParam[newValue]);
    filters = localStorage.getItem(`userPortfolioRequestListFiltersSelection${newValue}`);
    if (!user?.id) return;

    if (filters) {
      const parsedFilters = JSON.parse(filters)[user?.id];
      if (parsedFilters?.selectedProducts?.length) {
        setSelectedProducts(parsedFilters?.selectedProducts);
        setProductsExternalFilter({
          field: 'userPortfolio.subscribedPortfolio.productType.nameEn',
          operator: 'isAnyOf',
          id: uuid4(),
          value: parsedFilters?.selectedProducts,
        });
      } else onSelectProduct('Clear All');
      if (parsedFilters?.selectedFundCategories?.length) {
        setSelectedFundCategories(parsedFilters?.selectedFundCategories);
        setFundCategoriesExternalFilter({
          field: 'fundCategory.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: parsedFilters?.selectedFundCategories,
        });
      } else onSelectFundCategory('Clear All');
      if (parsedFilters?.selectedStatuses?.length) {
        setSelectedStatuses(parsedFilters?.selectedStatuses);
        setStatusesExternalFilter({
          field: 'status.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: parsedFilters?.selectedStatuses,
        });
      } else onSelectStatus('Clear All');
    } else {
      clearAllFilters();
    }
  };

  useEffect(() => {
    const filtersButton = Array.from(document.querySelectorAll('button[type="button"]')).find(
      (el) => el?.textContent?.includes('Filters'),
    );

    if (filtersButton) {
      // Create a MutationObserver to watch for changes in the DOM
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (mutation.type === 'childList' || mutation.type === 'subtree') {
            // Look for the "Remove all" button in the entire document
            const removeAllButton = Array.from(document.querySelectorAll('button[type="button"]')).find(
              (el) => el?.textContent?.includes('Remove all') && el.contains(el.firstChild),
            );

            if (removeAllButton) {
              // Stop observing once the button is found

              // Optional: Add click handler to the remove all button
              removeAllButton.addEventListener('click', () => {
                clearAllFilters();
                const urlObj = new URL(window.location.href);
                const tabValue = urlObj.searchParams.get('tab');

                localStorage.removeItem(
                  `userPortfolioRequestListFiltersSelection${mapTabParamToValue[tabValue || '']}`,
                );
                // Add your click logic here
              });

              observer.disconnect();
              break;
            }
          }
        }
      });

      // Configure the observer to watch the entire document for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }, [products]);

  useEffect(() => {
    if (!user?.id || !initiateFilters) return;
    setInitiateFilters(false);
    clearAllFilters();

    if (filters) {
      const parsedFilters = JSON.parse(filters)[user?.id];
      if (parsedFilters?.selectedProducts?.length) {
        setSelectedProducts(parsedFilters?.selectedProducts);
        setProductsExternalFilter({
          field: 'userPortfolio.subscribedPortfolio.productType.nameEn',
          operator: 'isAnyOf',
          id: uuid4(),
          value: parsedFilters?.selectedProducts,
        });
      } else onSelectProduct('Clear All');
      if (parsedFilters?.selectedFundCategories?.length) {
        setSelectedFundCategories(parsedFilters?.selectedFundCategories);
        setFundCategoriesExternalFilter({
          field: 'fundCategory.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: parsedFilters?.selectedFundCategories,
        });
      } else onSelectFundCategory('Clear All');
      if (parsedFilters?.selectedStatuses?.length) {
        setSelectedStatuses(parsedFilters?.selectedStatuses);
        setStatusesExternalFilter({
          field: 'status.name',
          operator: 'isAnyOf',
          id: uuid4(),
          value: parsedFilters?.selectedStatuses,
        });
      } else onSelectStatus('Clear All');
    }
    setFiltersReady(true);
  }, [user?.id]);

  if (!user?.id) return null;

  try {
    return (
      <Container component="main" maxWidth="xl">
        <Box sx={{ mb: '16px' }}>
          <Typography
            component="h1"
            sx={{ fontSize: '1.375rem', fontWeight: 600, color: COLORS.MAIN_DARK, lineHeight: '30px' }}
          >
            User Portfolio Requests
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY, mt: '2px' }}>
            Review and action subscription and redemption requests
          </Typography>
        </Box>

        {/* One row instead of a stack of chip rows, so the table starts near the top. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            p: '12px 16px',
            mb: '16px',
            borderRadius: '12px',
            border: `1px solid ${CARD_BORDER}`,
            backgroundColor: COLORS.WHITE,
          }}
        >
          <Typography component="div" sx={filterLabelSx}>
            Filters
          </Typography>

          <MultiSelectFilter
            label="Products"
            options={products}
            selected={selectedProducts}
            onToggle={onSelectProduct}
            selectedTab={selectedTab}
          />
          <MultiSelectFilter
            label="Fund Category"
            options={fundCategories}
            selected={selectedFundCategories}
            onToggle={onSelectFundCategory}
            selectedTab={selectedTab}
            color="success"
          />
          <MultiSelectFilter
            label="Status"
            options={statuses}
            selected={selectedStatuses}
            onToggle={onSelectStatus}
            selectedTab={selectedTab}
            color="warning"
          />

          <Box sx={{ flex: '1 1 auto' }} />
          <Typography sx={{ fontSize: '0.8125rem', color: COLORS.LIGHT_GRAY, whiteSpace: 'nowrap' }}>
            {activeFilterCount ? `${activeFilterCount} selected` : 'No filters applied'}
          </Typography>
          <Button
            type="button"
            size="small"
            variant="text"
            color="inherit"
            startIcon={<RestartAltIcon />}
            onClick={resetFilters}
            disabled={!activeFilterCount}
            sx={resetButtonSx}
          >
            Reset
          </Button>
        </Box>

        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          <TabContext value={mapTabParamToValue[selectedTab]}>
            <Box sx={{ borderBottom: `1px solid ${CARD_BORDER}`, mb: '16px' }}>
              <TabList
                value={mapTabParamToValue[selectedTab]}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                aria-label="user portfolio request list tabs"
              >
                <Tab
                  icon={<TrendingUpIcon />}
                  iconPosition="start"
                  label="Subscription"
                  value="1"
                  sx={{ textTransform: 'none', fontWeight: 600, minHeight: '48px' }}
                  disableRipple
                  disabled={isLoading}
                />
                <Tab
                  icon={<TrendingDownIcon />}
                  iconPosition="start"
                  label="Redemption"
                  value="2"
                  sx={{ textTransform: 'none', fontWeight: 600, minHeight: '48px' }}
                  disableRipple
                  disabled={isLoading}
                />
              </TabList>
            </Box>
            <MTabPanel value="1">
              {(selectedTab === mapTabValueToTabParam[1] || selectedTab === 'default') && (
                <UserPortfolioRequestTable
                  fetchEnabled={filtersReady}
                  type={UserPortfolioRequestType.SUBSCRIPTION}
                  setProducts={(ps) => setProducts(ps)}
                  setFundCategories={(fcs) => setFundCategories(fcs)}
                  setStatuses={(fcs) => setStatuses(fcs)}
                  externalFilter={[
                    { ...productsExternalFilter },
                    { ...fundCategoriesExternalFilter },
                    { ...statusesExternalFilter },
                  ].filter((filter) => Object.keys(filter).length > 0)}
                />
              )}
            </MTabPanel>
            <MTabPanel value="2">
              {selectedTab === mapTabValueToTabParam[2] && (
                <UserPortfolioRequestTable
                  fetchEnabled={filtersReady}
                  type={UserPortfolioRequestType.REDEMPTION}
                  setProducts={(ps) => setProducts(ps)}
                  setFundCategories={(fcs) => setFundCategories(fcs)}
                  setStatuses={(fcs) => setStatuses(fcs)}
                  externalFilter={[
                    { ...productsExternalFilter },
                    { ...fundCategoriesExternalFilter },
                    { ...statusesExternalFilter },
                  ].filter((filter) => Object.keys(filter).length > 0)}
                />
              )}
            </MTabPanel>
          </TabContext>
        </Box>
        {/* Still blocks the whole screen while a request is in flight, as before. */}
        <Backdrop
          open={isUserPortfolioRequestLoading}
          sx={{ zIndex: 1002, backgroundColor: alpha(COLORS.WHITE, 0.75) }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              px: '20px',
              py: '16px',
              borderRadius: '12px',
              backgroundColor: COLORS.WHITE,
              border: `1px solid ${CARD_BORDER}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            <CircularProgress size={22} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: COLORS.MAIN_DARK }}>
              Processing request…
            </Typography>
          </Box>
        </Backdrop>
      </Container>
    );
  } catch (e: any) {
    return (
      <Container component="main" maxWidth="xl">
        <Box sx={{ pb: 5 }}>
          <Typography variant="h4">User Portfolio Requests</Typography>
        </Box>
        <Typography>Unexpected error happens: {e.message}</Typography>
      </Container>
    );
  }
}
